<template>
  <!-- Work-order list filters (Appointments, S&R, RQ, UQ; last in each list's toolbar): funnel button with the applied-filter count, opening a
       right-side drawer (90% height) built from the list's filter config (POST config, backend G-49).
       Save / Clear write the filters to the URL (f.<param>…); the list (listQueryState) reloads from it. -->
  <div v-if="list" class="relative">
    <!-- filled (purple) while filters are applied -->
    <button type="button" aria-label="Filters"
      class="relative h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-full shadow-[0px_0px_10px_0px_#00000026] transition-colors border-0"
      :class="count ? 'bg-[#8479E1] hover:bg-[#6F63D4]' : 'bg-[#fff] hover:bg-[#F3F0FF]'" @click="open">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 5h18l-7 8.5V19l-4 2v-7.5L3 5z" :fill="count ? '#FFFFFF' : 'none'"
          :stroke="count ? '#FFFFFF' : '#8479E1'" stroke-width="1.8" stroke-linejoin="round" />
      </svg>
      <span v-if="count"
        class="absolute -top-[4px] -right-[4px] min-w-[18px] h-[18px] px-[4px] rounded-full bg-[#F67280] text-white text-[11px] leading-[18px] text-center font-roboto">
        {{ count }}
      </span>
    </button>

    <div ref="modal" class="modal fade listFilterModal" tabindex="-1" aria-label="Filters" aria-hidden="true">
      <div
        class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!right-0 mobile:!px-3 tablet:px-0 tablet:!w-[720px] tablet:!max-w-[calc(100vw-32px)] !absolute tablet:!right-4 !top-0 !m-0 h-full">
        <div class="modal-content">
          <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
            <FxModalHeader :title="title" :dismiss="false" @close="close" />

            <!-- 720px wide = the date range picker with its calendars; date range on its own row, the rest 2 per row -->
            <div class="flex-1 grid grid-cols-1 tablet:grid-cols-2 content-start gap-x-[16px] gap-y-[16px] px-[6px]">
              <p v-if="loading" class="tablet:col-span-2 m-auto py-[40px] text-[14px] text-[#999999]">Loading filters…
              </p>
              <p v-else-if="!filters.length"
                class="tablet:col-span-2 m-auto py-[40px] text-center text-[14px] leading-[20px] text-[#999999]">
                Filters aren't available for this list yet.
              </p>
              <div v-for="f in filters" v-else :key="f.key"
                :class="{ 'tablet:col-span-2': f.fieldType === 'date_range' }">
                <span class="uqcLabel">{{ f.title }}</span>
                <!-- date range: the Laravel Pro panel picker (presets, Custom Range opens the calendars) -->
                <DateRangePicker v-if="f.fieldType === 'date_range'" :model-value="rangeOf(f)" :showCalendar="true"
                  @update:model-value="v => setSingle(f, v && v.length === 2 ? `${v[0]} - ${v[1]}` : '')" />
                <!-- typed input (Min / Max Amount): project input as text like the Laravel customInput — digits, one ".",
                     2 decimals for float, max 10 characters, no "-" / "+" / "e" (a type=number field reads "12." as '') -->
                <inputComponent v-else-if="f.fieldType === 'input'" :name="'filter-' + f.key" type="text"
                  :inputmode="isNumeric(f) ? 'decimal' : null" placeholder="Enter"
                  :model-value="(draft[qKey(f)] || [])[0] || ''" :custom-class="fieldClass"
                  @keydown="e => isNumeric(f) && numberKeydown(e, f)"
                  @update:model-value="v => setSingle(f, isNumeric(f) ? cleanNumber(v, f) : v)" />
                <!-- dropdowns like the Laravel Pro panel: multi = check boxes + Select All + search; single = one value,
                     ✕ clears (job types only for the picked trades when depends_on) -->
                <ProMultiSelectDropdown v-else-if="f.isMultiple" :options="visibleOptions(f)" :field-name="f.title"
                  :model-value="draft[qKey(f)] || []" @update:model-value="v => setMulti(f, v)" />
                <ProSingleSelectDropdown v-else :options="f.options" :field-name="f.title"
                  :model-value="(draft[qKey(f)] || [])[0] ?? null"
                  @update:model-value="v => setSingle(f, v == null ? '' : v)" />
              </div>
            </div>

            <!-- footer: Clear Filter · Save -->
            <div class="uqcFooter flex items-center justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" :disabled="!count && !draftCount" @click="clearAll">
                Clear Filter
              </button>
              <button type="button" class="uqcBtnNext" :disabled="loading" @click="save">
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import DateRangePicker from "./DateRangePicker.vue";
import inputComponent from "./inputComponent.vue";
import ProMultiSelectDropdown from "./ProMultiSelectDropdown.vue";
import ProSingleSelectDropdown from "./ProSingleSelectDropdown.vue";
import { listFilterApi } from "@/services/listFilterApi";
import { LIST_BY_ROUTE, filtersFromQuery, queryWithFilters, filterCount, filterQueryKey, defaultFilters } from "@/helpers/listFilters";
import { disableScroll, enableScroll } from "@/helpers/scrollHelper";

const TITLES = { appointment: "Appointment Filters", sar: "Service & Repair Filters", rq: "Replacement Quote Filters", uq: "Universal Quote Filters" };

export default {
  components: { FxModalHeader, DateRangePicker, inputComponent, ProMultiSelectDropdown, ProSingleSelectDropdown },
  data() {
    return {
      filters: [], loading: false, draft: {}, modal: null,
      // every filter field: 40px, 8px radius (same as the dropdowns and the date range)
      fieldClass: '!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333] focus:!border-[#8479E1]',
    };
  },
  computed: {
    list() {
      return LIST_BY_ROUTE[this.$route.name] || null;
    },
    title() {
      return TITLES[this.list] || "Filters";
    },
    // applied filters (in the URL) → badge
    count() {
      return filterCount(filtersFromQuery(this.$route.query));
    },
    draftCount() {
      return filterCount(this.draft);
    },
  },
  beforeUnmount() {
    if (this.modal) this.modal.hide();
  },
  methods: {
    qKey(f) {
      return filterQueryKey(f);
    },
    async open() {
      this.draft = JSON.parse(JSON.stringify(filtersFromQuery(this.$route.query)));
      if (!this.modal) this.modal = new Modal(this.$refs.modal, { backdrop: "static", keyboard: false });
      this.modal.show();
      disableScroll();
      this.loading = true;
      const res = await listFilterApi.config(this.list);
      this.filters = res.filters;
      this.loading = false;
    },
    close() {
      if (this.modal) this.modal.hide();
      enableScroll();
    },
    setMulti(f, values) {
      this.draft = { ...this.draft, [this.qKey(f)]: (values || []).map(String) };
    },
    // "MM/DD/YYYY - MM/DD/YYYY" → [start, end] for the picker
    rangeOf(f) {
      const v = (this.draft[this.qKey(f)] || [])[0];
      return v ? v.split(" - ") : [];
    },
    isNumeric(f) {
      return ["float", "number", "integer"].includes(f.inputType);
    },
    // like the Laravel customInput: keys that can't make a valid number are blocked while typing
    numberKeydown(e, f) {
      const ctrl = ["Backspace", "Delete", "Tab", "Enter", "Escape", "ArrowLeft", "ArrowRight", "Home", "End"];
      if (ctrl.includes(e.key) || e.ctrlKey || e.metaKey) return;
      const el = e.target;
      const selected = el.selectionEnd - el.selectionStart;
      const value = el.value;
      if (e.key === ".") {
        if (f.inputType !== "float" || value.includes(".")) e.preventDefault();
        return;
      }
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
        return;
      }
      if (selected > 0) return;
      if (value.length >= 10) {
        e.preventDefault();
        return;
      }
      const dot = value.indexOf(".");
      if (dot !== -1 && el.selectionStart > dot && value.length - dot - 1 >= 2) e.preventDefault();
    },
    // pasted / dropped text: digits and one "." (float), 2 decimals, 10 characters
    cleanNumber(v, f) {
      let s = String(v ?? "").replace(f.inputType === "float" ? /[^\d.]/g : /\D/g, "");
      const dot = s.indexOf(".");
      if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "").slice(0, 2);
      return s.slice(0, 10);
    },
    setSingle(f, value) {
      this.draft = { ...this.draft, [this.qKey(f)]: value == null || value === "" ? [] : [String(value)] };
    },
    // options of a dependent block (UQ job types → picked trades); all when nothing is picked
    visibleOptions(f) {
      if (!f.dependsOn) return f.options;
      const parent = this.filters.find(x => x.key === f.dependsOn);
      const picked = parent ? this.draft[this.qKey(parent)] || [] : [];
      return picked.length ? f.options.filter(o => !o.tradeId || picked.includes(o.tradeId)) : f.options;
    },
    apply(filters) {
      this.$router.replace({ path: this.$route.path, query: queryWithFilters(this.$route.query, filters) }).catch(() => { });
      this.close();
    },
    save() {
      this.apply(this.draft);
    },
    // back to the defaults (Date Range = Year to Date)
    clearAll() {
      this.draft = defaultFilters();
      this.apply({});
    },
  },
};
</script>
